import type { BarPoint } from '../api/marketData'
import { adx, ema, sma } from './indicators/math'
import { getDefaultConfig } from './indicators/defaultPreset'
import { useSettingsStore } from '../stores/useSettingsStore'

export const TREND_BLOCK_KEY = 'trend-block-enabled'
export const TREND_STRENGTH_KEY = 'trend-strength-config'

/** Storage key the TickerPriceChart writes its indicator config to. */
const INDICATORS_STORAGE_KEY = 'ticker-chart-indicators'

export type TrendDir = 'up' | 'down' | 'sideways'

export interface TrendMA {
  type: 'ema' | 'sma'
  period: number
}

/**
 * Optional gates layered on top of strict MA-stack alignment. Any filter set
 * to a positive number must agree with the alignment direction; a null/zero
 * value disables that filter. Failing any active filter demotes the verdict
 * from up/down to sideways (i.e., the rule blocks fewer trades, not more —
 * weak/ambiguous trends just don't count).
 */
export interface TrendStrengthConfig {
  /** Min spread between fastest and slowest MA, expressed as % of slowest MA. */
  minSeparationPct: number | null
  /** Slowest MA must be moving in the trend direction over the last N bars. */
  minSlopeBars: number | null
  /** Min ADX(14) value. Classic strength gauge. */
  minAdx: number | null
}

export const TREND_STRENGTH_DEFAULT: TrendStrengthConfig = {
  minSeparationPct: null,
  minSlopeBars: null,
  minAdx: null,
}

export type TrendStrengthPresetId = 'off' | 'loose' | 'balanced' | 'strict'

export interface TrendStrengthPreset {
  id: TrendStrengthPresetId
  label: string
  description: string
  config: TrendStrengthConfig
}

/** Named starting points; user can still tweak any field after applying. */
export const TREND_STRENGTH_PRESETS: readonly TrendStrengthPreset[] = [
  {
    id: 'off',
    label: 'Off',
    description: 'No strength filters — strict MA-stack alignment only.',
    config: { minSeparationPct: null, minSlopeBars: null, minAdx: null },
  },
  {
    id: 'loose',
    label: 'Loose',
    description: 'Just kill flat-stack false positives.',
    config: { minSeparationPct: 0.15, minSlopeBars: null, minAdx: null },
  },
  {
    id: 'balanced',
    label: 'Balanced',
    description: 'Textbook intraday thresholds.',
    config: { minSeparationPct: 0.25, minSlopeBars: 5, minAdx: 20 },
  },
  {
    id: 'strict',
    label: 'Strict',
    description: 'Only count obvious, supported trends.',
    config: { minSeparationPct: 0.4, minSlopeBars: 8, minAdx: 25 },
  },
]

/** Returns the preset whose values match `cfg` exactly, or null if custom. */
export function matchTrendStrengthPreset(
  cfg: TrendStrengthConfig,
): TrendStrengthPresetId | null {
  for (const p of TREND_STRENGTH_PRESETS) {
    if (
      p.config.minSeparationPct === cfg.minSeparationPct &&
      p.config.minSlopeBars === cfg.minSlopeBars &&
      p.config.minAdx === cfg.minAdx
    ) {
      return p.id
    }
  }
  return null
}

export function parseTrendStrength(raw: unknown): TrendStrengthConfig {
  if (typeof raw !== 'object' || raw == null || Array.isArray(raw)) {
    return { ...TREND_STRENGTH_DEFAULT }
  }
  const obj = raw as Record<string, unknown>
  const sep = typeof obj.minSeparationPct === 'number' && obj.minSeparationPct > 0
    ? obj.minSeparationPct
    : null
  const slope = typeof obj.minSlopeBars === 'number' && obj.minSlopeBars > 0
    ? Math.floor(obj.minSlopeBars)
    : null
  const adxMin = typeof obj.minAdx === 'number' && obj.minAdx > 0 ? obj.minAdx : null
  return { minSeparationPct: sep, minSlopeBars: slope, minAdx: adxMin }
}

export function loadTrendStrength(): TrendStrengthConfig {
  try {
    const raw = useSettingsStore.getState().values[TREND_STRENGTH_KEY]
    if (raw != null) return parseTrendStrength(JSON.parse(raw))
  } catch {
    /* fall through */
  }
  return { ...TREND_STRENGTH_DEFAULT }
}

/**
 * Read the user's enabled EMA/SMA indicators from the chart's stored indicator
 * config (settings store, synced to the DB). Other indicator types (vwap, orb,
 * …) are ignored. If nothing is stored yet, fall back to the intraday defaults
 * (EMA9/34/89) so the rule does something useful before the user has visited a
 * ticker chart.
 */
export function loadTrendMAs(): TrendMA[] {
  try {
    const raw = useSettingsStore.getState().values[INDICATORS_STORAGE_KEY]
    if (raw != null) {
      const parsed = JSON.parse(raw) as {
        indicators?: Array<{ type?: string; enabled?: boolean; period?: number }>
      }
      const mas: TrendMA[] = []
      for (const i of parsed.indicators ?? []) {
        if ((i.type === 'ema' || i.type === 'sma') && i.enabled && typeof i.period === 'number') {
          mas.push({ type: i.type, period: i.period })
        }
      }
      if (mas.length > 0) return mas
    }
  } catch {
    /* fall through to defaults */
  }
  const defaults = getDefaultConfig('1m').indicators
  const out: TrendMA[] = []
  for (const i of defaults) {
    if ((i.type === 'ema' || i.type === 'sma') && i.enabled) {
      out.push({ type: i.type, period: i.period })
    }
  }
  return out
}

/**
 * Strict MA-stack alignment trend.
 *
 *  - Sort by period ascending (fastest → slowest).
 *  - `up` iff every adjacent pair satisfies fast > slow.
 *  - `down` iff every adjacent pair satisfies fast < slow.
 *  - Anything else (including <2 MAs, insufficient bars, or any value that
 *    fails the strict ordering) → `sideways`.
 *
 * Returns `sideways` rather than throwing so callers can treat it as
 * "no decision — let the trade through".
 */
export function computeTrend(
  bars: BarPoint[],
  mas: TrendMA[],
  strength: TrendStrengthConfig = TREND_STRENGTH_DEFAULT,
): TrendDir {
  if (bars.length === 0 || mas.length < 2) return 'sideways'

  const sorted = [...mas].sort((a, b) => a.period - b.period)
  const maxPeriod = sorted[sorted.length - 1].period
  if (bars.length < maxPeriod) return 'sideways'

  const closes = bars.map((b) => b.c)
  const lastVals: number[] = []
  for (const ma of sorted) {
    const series = ma.type === 'ema' ? ema(closes, ma.period) : sma(closes, ma.period)
    const v = series[series.length - 1]
    if (v == null || !Number.isFinite(v)) return 'sideways'
    lastVals.push(v)
  }

  let allDesc = true // up: fast > mid > slow
  let allAsc = true // down: fast < mid < slow
  for (let i = 1; i < lastVals.length; i++) {
    if (!(lastVals[i - 1] > lastVals[i])) allDesc = false
    if (!(lastVals[i - 1] < lastVals[i])) allAsc = false
  }
  let base: TrendDir
  if (allDesc) base = 'up'
  else if (allAsc) base = 'down'
  else return 'sideways'

  // Separation: |fast - slow| / slow * 100 must clear the threshold.
  if (strength.minSeparationPct != null) {
    const fast = lastVals[0]
    const slow = lastVals[lastVals.length - 1]
    if (!(slow > 0)) return 'sideways'
    const sep = (Math.abs(fast - slow) / slow) * 100
    if (sep < strength.minSeparationPct) return 'sideways'
  }

  // Slope: slowest MA value `minSlopeBars` ago must compare in the right
  // direction against the current value.
  if (strength.minSlopeBars != null) {
    const slowest = sorted[sorted.length - 1]
    const series = slowest.type === 'ema' ? ema(closes, slowest.period) : sma(closes, slowest.period)
    const k = strength.minSlopeBars
    if (series.length <= k) return 'sideways'
    const cur = series[series.length - 1]
    const prev = series[series.length - 1 - k]
    if (cur == null || prev == null) return 'sideways'
    if (base === 'up' && !(cur > prev)) return 'sideways'
    if (base === 'down' && !(cur < prev)) return 'sideways'
  }

  // ADX(14): classic strength gauge. Requires ~2 * length bars of warm-up.
  if (strength.minAdx != null) {
    const adxSeries = adx(bars, 14)
    const v = adxSeries[adxSeries.length - 1]
    if (v == null || !Number.isFinite(v) || v < strength.minAdx) return 'sideways'
  }

  return base
}
